import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import FormSpinnerOverlay from '../../components/ui/FormSpinnerOverlay';
import AlertMessage from '../../components/ui/AlertMessage';
import DynamicTable from '../../components/Table/DynamicTable';
import Pagination from '../../components/Pagination/Pagination';
import SearchIcon from '../../components/icons/SearchIcon';
import EyeIcon from '../../components/icons/EyeIcon';
import PencilFillIcon from '../../components/icons/PencilFillIcon';
import TrashFillIcon from '../../components/icons/TrashFillIcon'; // Assuming this icon component exists or will be created
// import AddIcon from '../../components/icons/AddIcon'; // If you need an "Add User" button

const UserListAdminScreen = () => {
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
    const { token } = useAuth();
    const [users, setUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [filterRole, setFilterRole] = useState('');
    const itemsPerPage = 10; // Number of items per page
    const navigate = useNavigate();

    const roleMappings = { // Added for linking
        admin: { plural: 'admins', singular: 'admin', translated: 'Administrador' },
        agent: { plural: 'agents', singular: 'agent', translated: 'Asesor' },
        supplier: { plural: 'suppliers', singular: 'supplier', translated: 'Proveedor' },
        user: { plural: 'users', singular: 'user', translated: 'Cliente' },
    };

    // Options for the role filter, derived from roleMappings
    const roleOptions = [
        { value: "", label: "Filtrar por rol" },
        ...Object.keys(roleMappings).map(key => ({
            value: roleMappings[key].translated, label: roleMappings[key].translated
        }))
    ];

    useEffect(() => {
        const fetchUsers = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const endpoints = [
                    `${API_BASE_URL}/admins`,
                    `${API_BASE_URL}/agents`,
                    `${API_BASE_URL}/suppliers`,
                    `${API_BASE_URL}/users`
                ];

                // Keys corresponding to each endpoint, used for role lookup and consistent ID generation
                const endpointRoleKeys = [
                    'admin',
                    'agent',
                    'supplier',
                    'user'
                ];

                // roleTranslations is now part of roleMappings

                const fetchPromises = endpoints.map(endpoint =>
                    fetch(`${API_BASE_URL}/api/${endpoint.split('/').pop()}`, {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        },
                    }).then(response => {
                        if (!response.ok) {
                            // Try to parse error, default to status text
                            return response.json().catch(() => ({ message: `Error fetching from ${endpoint}: ${response.statusText}` }))
                                .then(errorData => {
                                    throw new Error(errorData.message || `Error fetching from ${endpoint}: ${response.statusText}`);
                                });
                        }
                        return response.json();
                    })
                );

                const results = await Promise.all(fetchPromises);

                let allUsers = [];
                results.forEach((userArray, endpointIndex) => {
                    const baseRoleKey = endpointRoleKeys[endpointIndex];
                    const translatedDisplayRole = roleMappings[baseRoleKey]?.translated || baseRoleKey;

                    const usersWithRole = userArray.map((user, itemIndex) => ({
                        ...user,
                        name: user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'N/A',
                        role: user.role || translatedDisplayRole,
                        baseRoleKey: baseRoleKey,
                        renderKey: `${baseRoleKey}-${user.id || `item-${itemIndex}`}`,
                        created_at: user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A',
                    }));
                    allUsers = allUsers.concat(usersWithRole);
                });
                setUsers(allUsers.reverse());
            } catch (err) {
                setError(err.message);
                console.error("Error fetching users:", err);
            } finally {
                setIsLoading(false);
            }
        };

        if (token) {
            fetchUsers();
        }
    }, [API_BASE_URL, token]); // Removed itemsPerPage from dependency array as it's a constant

    // Define columns for the DynamicTable
    const userColumns = [
        { header: 'Nombre Completo', accessor: 'name' },
        { header: 'Correo electrónico', accessor: 'email' },
        { header: 'Rol del usuario', accessor: 'role' },
        { header: 'Fecha de Registro', accessor: 'created_at' }
    ];

    // Define actions for the DynamicTable
    const userActions = [
        {
            icon: <EyeIcon />,
            tooltip: 'Ver Usuario',
            className: 'btn-outline-info-checkin',
            onClick: (user) => console.log('View user:', user.id) // Replace with actual navigation/modal logic
        },
        {
            icon: <PencilFillIcon />,
            tooltip: 'Editar Usuario',
            className: 'btn-outline-warning-checkin',
            onClick: (user) => {
                // Navigate to edit page using user.baseRoleKey and user.id
                console.log('Edit user:', user.id, 'Role:', user.baseRoleKey);
                // Example navigation: history.push(`/users/edit/${user.baseRoleKey}/${user.id}`);
                navigate(`/users/edit/${user.baseRoleKey}/${user.id}`);
            }
        },
        {
            icon: <TrashFillIcon />,
            tooltip: 'Eliminar Usuario',
            className: 'btn-outline-danger-checkin',
            onClick: (user) => console.log('Delete user:', user.id) // Replace with actual deletion logic
        },
    ];

    // Define which columns are searchable
    const searchableColumns = ['name', 'email', 'role'];

    // Apply search and filter logic
    const filteredUsers = users.filter(user => {
        // 1. Filter by search term across specified columns
        const matchesSearchTerm = searchTerm.trim() === '' ||
            searchableColumns.some(column => {
                const value = String(user[column]).toLowerCase();
                return value.includes(searchTerm.toLowerCase());
            });

        // 2. Filter by role
        const matchesRole = filterRole === '' || user.role === filterRole;


        return matchesSearchTerm && matchesRole;
    });

    const totalItems = filteredUsers.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);

    // Calculate the start and end index for the current page
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedUsers = filteredUsers.slice(startIndex, endIndex);

    // Reset current page when search term changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, filterRole]);


    return (
        <>
            <FormSpinnerOverlay isLoading={isLoading} />
            {/* Header section with title and optional "Add User" button */}
            <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pt-3 pb-2 mb-3">
                <h1 className="h2 mb-0">Usuarios del Sistema</h1>
                {/* <div className="btn-toolbar mb-2 mb-md-0">
                    <Link to="/admin/users/add" className="btn btn-success d-flex align-items-center">
                        <AddIcon />
                        Añadir usuario
                    </Link>
                </div> */}
            </div>

            {error && <AlertMessage type="danger" message={`Error al cargar usuarios: ${error}`} />}

            {!isLoading && !error && users.length === 0 && (
                <AlertMessage type="info" message="No hay usuarios registrados en el sistema." />
            )}

            {/* Search Bar */}
            <div className="row g-3 mb-3 align-items-center d-flex justify-content-between">
                <div className="col-md-7 col-lg-8">
                    <div className="input-group">
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Buscar usuarios por nombre, correo, etc."
                            aria-label="Buscar usuarios"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <button className="btn btn-outline-secondary" type="button">
                            <SearchIcon />
                        </button>
                    </div>
                </div>
                <div className="col-md-5 col-lg-4">
                    {/* Role Filter */}
                    <select
                        className="form-select"
                        id="filtroRolUsuario"
                        value={filterRole}
                        onChange={(e) => setFilterRole(e.target.value)}
                    >
                        {roleOptions.map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="row text-start mb-4">
                <div className="col-12">
                    <div className="card card-metric border-0">
                        <div className="card-body px-md-2 px-0">
                            <DynamicTable
                                data={paginatedUsers}
                                columns={userColumns}
                                actions={userActions}
                                keyAccessor="renderKey" // Use renderKey for unique identification
                            />
                            {totalPages > 1 && (
                                <Pagination
                                    currentPage={currentPage}
                                    itemsPerPage={itemsPerPage}
                                    totalItems={totalItems}
                                    onPageChange={(page) => setCurrentPage(page)}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>

        </>
    );
};

export default UserListAdminScreen;